import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { SoonBadge } from './Badge';
import { cx } from './cx';

export type MenuEntry =
  | {
      kind?: 'item';
      label: string;
      icon?: LucideIcon;
      /** Texte discret à droite (raccourci, précision). */
      hint?: string;
      danger?: boolean;
      disabled?: boolean;
      /** Affiche la pastille « Bientôt » (implique `disabled`). */
      soon?: boolean;
      onSelect?: () => void;
    }
  | { kind: 'label'; label: string }
  | { kind: 'separator' };

interface MenuProps {
  /** Rendu du déclencheur ; reçoit les attributs à poser sur le bouton. */
  trigger: (props: {
    ref: (el: HTMLButtonElement | null) => void;
    onClick: () => void;
    'aria-haspopup': 'menu';
    'aria-expanded': boolean;
    'aria-controls': string;
  }) => ReactNode;
  entries: MenuEntry[];
  align?: 'start' | 'end';
  className?: string;
  /** Ouverture et fermeture : le parent peut lire à la demande ce que le menu affiche. */
  onOpenChange?: (open: boolean) => void;
}

/** Menu déroulant ancré sous son déclencheur (clavier, Échap, clic extérieur). */
export function Menu({ trigger, entries, align = 'end', className, onOpenChange }: MenuProps) {
  const [open, setOpen] = useState(false);
  const notify = useRef(onOpenChange);
  notify.current = onOpenChange;
  useEffect(() => notify.current?.(open), [open]);
  const id = useId();
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Liste hors de l'écran (bouton à gauche sur téléphone, à droite sur tablette) : décalée pour rester visible.
  const [shift, setShift] = useState(0);
  useLayoutEffect(() => {
    if (!open) return setShift(0);
    const rect = listRef.current?.getBoundingClientRect();
    if (!rect) return;
    const margin = 8;
    const right = window.innerWidth - margin;
    setShift(rect.left < margin ? margin - rect.left : rect.right > right ? right - rect.right : 0);
  }, [open]);

  const items = () =>
    Array.from(listRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not([disabled])') ?? []);

  useEffect(() => {
    if (!open) return;
    items()[0]?.focus();
    const onPointer = (event: PointerEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointer);
    return () => document.removeEventListener('pointerdown', onPointer);
  }, [open]);

  const close = (focusTrigger = true) => {
    setOpen(false);
    if (focusTrigger) triggerRef.current?.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const list = items();
    const index = list.indexOf(document.activeElement as HTMLButtonElement);
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      list[(index + 1) % list.length]?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      list[(index - 1 + list.length) % list.length]?.focus();
    } else if (event.key === 'Tab') {
      close(false);
    }
  };

  return (
    <div ref={wrapRef} className={cx('relative inline-flex', className)}>
      {trigger({
        ref: (el) => {
          triggerRef.current = el;
        },
        onClick: () => setOpen((value) => !value),
        'aria-haspopup': 'menu',
        'aria-expanded': open,
        'aria-controls': id,
      })}
      {open && (
        <div
          ref={listRef}
          id={id}
          role="menu"
          onKeyDown={onKeyDown}
          style={shift ? { translate: `${shift}px 0` } : undefined}
          className={cx(
            'absolute top-[calc(100%+8px)] z-[60] min-w-[250px] max-w-[calc(100vw-16px)] animate-pop rounded-row border border-line bg-white p-1.5 shadow-pop',
            align === 'end' ? 'right-0' : 'left-0',
          )}
        >
          {entries.map((entry, index) => {
            if (entry.kind === 'separator') return <div key={index} role="separator" className="mx-1 my-1.5 h-px bg-line" />;
            if (entry.kind === 'label')
              return (
                <p key={index} className="px-3 pb-1 pt-2.5 text-[10px] font-bold uppercase tracking-[.8px] text-muted">
                  {entry.label}
                </p>
              );
            const Icon = entry.icon;
            const disabled = entry.disabled || entry.soon;
            return (
              <button
                key={index}
                type="button"
                role="menuitem"
                disabled={disabled}
                onClick={() => {
                  entry.onSelect?.();
                  close();
                }}
                className={cx(
                  'group flex w-full items-center gap-2.5 whitespace-nowrap rounded-[10px] px-3 py-[9px] text-left text-[13px] outline-none max-sm:whitespace-normal',
                  disabled ? 'cursor-not-allowed opacity-50'
                  : entry.danger ? 'text-danger hover:bg-danger-light focus:bg-danger-light'
                  : 'text-body hover:bg-primary-50 hover:text-primary-700 focus:bg-primary-50 focus:text-primary-700',
                )}
              >
                {Icon && (
                  <Icon
                    size={16}
                    strokeWidth={1.9}
                    aria-hidden
                    className={cx(
                      'shrink-0',
                      entry.danger ? 'text-danger' : 'text-muted',
                      !disabled && !entry.danger && 'group-hover:text-primary group-focus:text-primary',
                    )}
                  />
                )}
                <span>
                  {entry.label}
                  {entry.soon && <SoonBadge />}
                </span>
                {entry.hint && <small className="ml-auto text-[11px] text-muted">{entry.hint}</small>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
